import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

function FriendPicker({
  selectedFriendIds,
  onAddFriend,
  onRemoveFriend,
  isOpen,
  user,
}) {
  const [friends, setFriends] = useState([]);
  const [friendSearch, setFriendSearch] = useState('');
  const [showFriendDropdown, setShowFriendDropdown] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const friendPickerRef = useRef(null);

  // Fetch friends when modal opens
  useEffect(() => {
    const fetchFriends = async () => {
      if (!isOpen || !user) return;

      setLoadingFriends(true);
      try {
        const currentUserId = user.id || user.userId;
        const response = await api.getFriends(currentUserId);
        const friendsList = (response.data || [])
          .map(item => item.user)
          .filter(Boolean);
        setFriends(friendsList);
      } catch (err) {
        console.error('Failed to fetch friends:', err);
      } finally {
        setLoadingFriends(false);
      }
    };

    fetchFriends();
  }, [isOpen, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (friendPickerRef.current && !friendPickerRef.current.contains(event.target)) {
        setShowFriendDropdown(false);
      }
    };

    if (showFriendDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFriendDropdown]);

  // Filter friends based on search and exclude already selected
  const filteredFriends = friends.filter((friend) => {
    const isNotSelected = !selectedFriendIds.includes(friend.id);
    const matchesSearch = friendSearch === '' ||
      (friend.displayName || '').toLowerCase().includes(friendSearch.toLowerCase()) ||
      (friend.username || '').toLowerCase().includes(friendSearch.toLowerCase());
    return isNotSelected && matchesSearch;
  });

  // Get selected friend objects for displaying pills
  const selectedFriends = friends.filter((friend) => selectedFriendIds.includes(friend.id));

  const handleAddFriend = (friend) => {
    onAddFriend(friend);
    setFriendSearch('');
    setShowFriendDropdown(false);
  };

  return (
    <div className="form-group">
      <label>Share with</label>
      <div className="friend-picker" ref={friendPickerRef}>
        <div className="friend-picker-input-container">
          {selectedFriends.map((friend) => (
            <span key={friend.id} className="friend-pill">
              {friend.displayName || friend.username}
              <button
                type="button"
                className="friend-pill-remove"
                onClick={() => onRemoveFriend(friend.id)}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            className="friend-search-input"
            placeholder={selectedFriends.length === 0 ? "Search friends..." : ""}
            value={friendSearch}
            onChange={(e) => {
              setFriendSearch(e.target.value);
              setShowFriendDropdown(true);
            }}
            onFocus={() => setShowFriendDropdown(true)}
          />
        </div>
        {showFriendDropdown && (
          <div className="friend-dropdown">
            {loadingFriends ? (
              <div className="friend-dropdown-message">Loading friends...</div>
            ) : friends.length === 0 ? (
              <div className="friend-dropdown-message">You don't have any friends yet.</div>
            ) : filteredFriends.length === 0 ? (
              <div className="friend-dropdown-message">No friends match your search</div>
            ) : (
              filteredFriends.map((friend) => (
                <button
                  key={friend.id}
                  type="button"
                  className="friend-dropdown-item"
                  onClick={() => handleAddFriend(friend)}
                >
                  <span className="friend-dropdown-name">{friend.displayName || friend.username}</span>
                  <span className="friend-dropdown-username">@{friend.username}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FriendPicker;
